import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Network, Activity, MonitorSmartphone, FileText, MessageSquareWarning, Ticket, Megaphone, UserCircle2, LifeBuoy, LogOut, Bell, Moon, Sun, Menu, X, ShieldCheck, Clock, Wallet, ShoppingCart } from 'lucide-react';
import { portalApi, PORTAL_TOKEN_KEY, fmtAgo } from './api';
import { StatusDot } from './ui';
import { NetixLogo, PortalFooter, WelcomeBanner, Marquee, ThemePicker, useColorTheme } from './brand';

const PortalCtx = createContext(null);
export const usePortal = () => useContext(PortalCtx);

const NAV = [
  { to: '/customer-portal', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/customer-portal/connection', label: 'My Connection', icon: Network },
  { to: '/customer-portal/traffic', label: 'Live Speed & Traffic', icon: Activity, leasedOnly: true },
  { to: '/customer-portal/devices', label: 'Connected Devices', icon: MonitorSmartphone, leasedOnly: true },
  { to: '/customer-portal/service', label: 'My Service Details', icon: FileText },
  { to: '/customer-portal/billing', label: 'Account & Billing', icon: Wallet },
  { to: '/customer-portal/orders', label: 'My Orders', icon: ShoppingCart },
  { to: '/customer-portal/complaint', label: 'Raise a Complaint', icon: MessageSquareWarning },
  { to: '/customer-portal/tickets', label: 'My Complaints & Tickets', icon: Ticket },
  { to: '/customer-portal/announcements', label: 'Service Announcements', icon: Megaphone },
  { to: '/customer-portal/profile', label: 'My Profile', icon: UserCircle2 },
  { to: '/customer-portal/help', label: 'Help & Support', icon: LifeBuoy },
];

const HeaderPill = ({ dot, children, testId }) => (
  <div className="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full text-sm" data-testid={testId}>
    {dot}<span>{children}</span>
  </div>
);

export default function PortalLayout() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [overview, setOverview] = useState(null);
  const [notifs, setNotifs] = useState({ notifications: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const [bell, setBell] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem('portalTheme') === 'dark');
  const [color, setColor] = useColorTheme();

  const refreshOverview = useCallback(async () => {
    try { const { data } = await portalApi.get('/api/portal/overview'); setOverview(data); } catch { /* handled by interceptor */ }
  }, []);
  const refreshNotifs = useCallback(async () => {
    try { const { data } = await portalApi.get('/api/portal/notifications?limit=20'); setNotifs(data); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem(PORTAL_TOKEN_KEY)) { navigate('/customer-portal/login'); return; }
    portalApi.get('/api/portal/me').then(({ data }) => setMe(data)).catch(() => {});
    refreshOverview(); refreshNotifs();
    const id = setInterval(() => { refreshOverview(); refreshNotifs(); }, 30000);
    return () => clearInterval(id);
  }, [navigate, refreshOverview, refreshNotifs]);

  useEffect(() => { localStorage.setItem('portalTheme', dark ? 'dark' : 'light'); }, [dark]);

  const logout = async () => {
    try { await portalApi.post('/api/portal/auth/logout'); } catch { /* ignore */ }
    localStorage.removeItem(PORTAL_TOKEN_KEY);
    navigate('/customer-portal/login');
  };
  const markRead = async () => { await portalApi.post('/api/portal/notifications/read'); refreshNotifs(); };

  const serviceId = me?.customer?.id;
  const switchService = async (cid) => {
    await portalApi.post('/api/portal/me/select-service', { customer_id: cid });
    const { data } = await portalApi.get('/api/portal/me'); setMe(data); refreshOverview();
  };
  const ctx = { me, overview, serviceId, refreshOverview, refreshNotifs, customer: me?.customer, account: me?.account, services: me?.services || [], switchService, isBroadband: !!me?.customer?.is_broadband };
  const conn = overview?.connection;
  const uptime = overview?.uptime?.['24h']?.uptime_pct;
  const navItems = NAV.filter((n) => !(n.leasedOnly && ctx.isBroadband));

  return (
    <PortalCtx.Provider value={ctx}>
      <div className={`portal-root ${dark ? 'dark' : ''}`} data-theme={color}>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 text-slate-900 dark:text-slate-100 flex" data-testid="portal-layout">
          {open && <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setOpen(false)} />}
          <aside className={`portal-sidebar fixed lg:sticky top-0 z-50 h-screen w-72 shrink-0 text-white flex flex-col transition-transform ${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`} data-testid="portal-sidebar">
            <div className="p-5 flex items-center justify-between border-b border-white/10">
              <NetixLogo className="h-9" testId="sidebar-logo" />
              <button className="lg:hidden" onClick={() => setOpen(false)} aria-label="Close menu"><X className="h-5 w-5" /></button>
            </div>
            <div className="px-5 pt-4 pb-2 text-[11px] font-bold uppercase tracking-widest text-blue-200/60">Customer Portal</div>
            <nav className="flex-1 overflow-y-auto px-3 pb-3 space-y-1" aria-label="Portal navigation">
              {navItems.map(({ to, label, icon: Icon, end }) => (
                <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)} data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                  className={({ isActive }) => `relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive ? 'portal-nav-active text-white' : 'text-blue-100/80 hover:bg-white/10 hover:text-white'}`}>
                  <Icon className="h-4 w-4" />{label}
                </NavLink>
              ))}
            </nav>
            <div className="m-3 rounded-xl bg-white/5 border border-white/10 p-3 text-xs text-blue-100/80 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-400" />Secure session · ISO 27001</div>
            <button onClick={logout} className="mx-3 mb-3 flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-200 hover:bg-red-500/20" data-testid="portal-logout-btn"><LogOut className="h-4 w-4" />Logout</button>
          </aside>

          <div className="flex-1 min-w-0 flex flex-col">
            <header className="portal-topbar sticky top-0 z-30 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
              <div className="px-4 md:px-8 h-16 flex items-center gap-3">
                <button className="lg:hidden p-2 rounded-lg hover:bg-white/15" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="portal-menu-btn"><Menu className="h-5 w-5" /></button>
                <Activity className="h-5 w-5 hidden sm:block" />
                <h1 className="text-base md:text-xl font-bold truncate">Netix Customer Portal</h1>
                <div className="hidden md:flex items-center gap-3 ml-4">
                  <HeaderPill testId="header-connection-pill" dot={<StatusDot state={conn?.state} size="h-2.5 w-2.5" />}><span className="font-semibold" data-testid="header-connection-label">{conn?.label || 'Checking…'}</span></HeaderPill>
                  {uptime != null && <HeaderPill testId="header-uptime-pill" dot={<span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />}>{uptime}% uptime 24h</HeaderPill>}
                  {conn?.last_check && <HeaderPill dot={<Clock className="h-3.5 w-3.5 text-blue-200" />}>checked {fmtAgo(conn.last_check)}</HeaderPill>}
                </div>
                <div className="ml-auto flex items-center gap-1 md:gap-2">
                  <ThemePicker theme={color} onChange={setColor} />
                  <button onClick={() => setDark(!dark)} className="p-2 rounded-lg hover:bg-white/15" aria-label="Toggle theme" data-testid="theme-toggle">{dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}</button>
                  <div className="relative">
                    <button onClick={() => { setBell(!bell); if (!bell && notifs.unread) markRead(); }} className="p-2 rounded-lg hover:bg-white/15 relative" aria-label="Notifications" data-testid="notifications-bell">
                      <Bell className="h-5 w-5" />
                      {notifs.unread > 0 && <span className="absolute -top-0.5 -right-0.5 h-5 min-w-5 px-1 rounded-full bg-[#C8102E] text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-indigo-700" data-testid="notifications-unread">{notifs.unread}</span>}
                    </button>
                    {bell && (
                      <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto rounded-2xl bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-800 shadow-2xl p-2 z-50" data-testid="notifications-panel">
                        <p className="px-3 py-2 text-xs font-bold uppercase text-slate-500">Notifications</p>
                        {notifs.notifications.length === 0 && <p className="px-3 py-6 text-sm text-slate-500 text-center">No notifications yet</p>}
                        {notifs.notifications.map((n) => (
                          <div key={n.id} className={`px-3 py-2 rounded-xl text-sm ${n.read ? '' : 'bg-blue-50 dark:bg-blue-900/20'}`}>
                            <p className="font-semibold">{n.title}</p><p className="text-slate-500 text-xs">{n.message}</p><p className="text-[11px] text-slate-400 mt-0.5">{fmtAgo(n.created_at)}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="hidden md:flex items-center gap-2 pl-2 border-l border-white/20">
                    <span className="h-9 w-9 rounded-full bg-white text-[#1E3A8A] text-sm font-black flex items-center justify-center shadow-md">{(me?.account?.name || 'C').slice(0, 1).toUpperCase()}</span>
                    <span className="text-sm font-semibold" data-testid="header-customer-name">{me?.account?.name}</span>
                  </div>
                </div>
              </div>
            </header>
            <main className="flex-1 p-4 md:p-8 max-w-7xl w-full mx-auto">
              <WelcomeBanner name={me?.account?.name} role="Customer" testId="portal-welcome-banner">
                {me?.services?.length > 1 && (
                  <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-slate-300" data-testid="service-switcher">
                    Service
                    <select value={serviceId || ''} onChange={(e) => switchService(e.target.value)} className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-sm font-bold text-slate-900 dark:text-white shadow-sm" data-testid="service-switcher-select">
                      {me.services.map((s) => <option key={s.id} value={s.id}>{s.service || s.service_type || 'Service'} · {s.plan_speed || s.plan || s.office_name || s.customer_name}{s.broadband_account_number ? ` · ${s.broadband_account_number}` : ''}</option>)}
                    </select>
                  </label>
                )}
                {me?.services?.length > 1 && <span className="hidden md:inline-flex px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-[11px] font-bold" data-testid="service-count">{me.services.length} services linked</span>}
                <div className={`relative bg-gradient-to-br rounded-lg px-3 py-1.5 shadow-lg text-white ${conn?.state === 'online' ? 'from-green-400 via-emerald-500 to-green-600' : conn?.state === 'offline' || conn?.state === 'service_inactive' ? 'from-red-500 to-rose-600' : 'from-slate-400 to-slate-600'}`} data-testid="banner-connection-tile">
                  <p className="text-xs font-semibold leading-tight">Connection</p>
                  <p className="text-lg font-black leading-tight">{conn?.label || '—'}</p>
                  {conn?.since && <p className="text-[10px] font-semibold leading-tight opacity-90" data-testid="banner-connection-since">for {fmtAgo(conn.since).replace(' ago', '')}</p>}
                </div>
                <div className="relative bg-gradient-to-br from-blue-500 via-indigo-600 to-purple-600 rounded-lg px-3 py-1.5 shadow-lg text-white">
                  <p className="text-xs font-semibold leading-tight">Uptime 24h</p>
                  <p className="text-lg font-black leading-tight">{uptime != null ? `${uptime}%` : '—'}</p>
                </div>
              </WelcomeBanner>
              <Marquee data={overview?.marquee} testId="customer-marquee" />
              <div className="portal-rise"><Outlet /></div>
            </main>
            <PortalFooter version="Customer Portal v1.0" testId="portal-footer" />
          </div>
        </div>
      </div>
    </PortalCtx.Provider>
  );
}
